import React from 'react';
import { Space, Tag, Tooltip, Typography } from 'antd';
import { CheckOutlined, MinusOutlined } from '@ant-design/icons';
import DocumentStatusBadge from '../../../../components/documents/DocumentStatusBadge';
import {
  resolveCompositionRowBadges,
  type CompositionRowSource,
} from '../../lib/compositionRowSource';
import type { ClosingPackageItemState, CompositionStatus } from '../../types';
import type { CompositionRowAction } from '../../lib/compositionRowActions';
import RowActions from './RowActions';

const { Text } = Typography;

export type CompositionRowColumn =
  | 'document'
  | 'type'
  | 'status'
  | 'file'
  | 'route'
  | 'required'
  | 'actions';

export type CompositionRowProps = {
  column: CompositionRowColumn;
  documentLabel: string;
  required: boolean;
  state: ClosingPackageItemState;
  compositionStatus: CompositionStatus;
  source: CompositionRowSource;
  isManual: boolean;
  fileName: string | null;
  documentTypeLabel: string | null;
  routeLabel: string | null;
  availableActions: CompositionRowAction[];
  onRowAction?: (action: CompositionRowAction) => void;
  actionLoading?: boolean;
};

/**
 * Dumb row cell renderer — receives ready props, no business logic.
 */
const CompositionRow: React.FC<CompositionRowProps> = ({
  column,
  documentLabel,
  required,
  state,
  compositionStatus,
  source,
  isManual: _isManual,
  fileName,
  documentTypeLabel,
  routeLabel,
  availableActions,
  onRowAction,
  actionLoading = false,
}) => {
  const isExcluded = compositionStatus === 'EXCLUDED';
  const badges = resolveCompositionRowBadges({ source, required, compositionStatus });

  switch (column) {
    case 'document':
      return (
        <Space size={6} wrap={false} className="cp-composition__document-cell">
          <Tooltip title={documentLabel}>
            <Text
              strong
              ellipsis
              type={isExcluded ? 'secondary' : undefined}
              className="cp-composition__ellipsis-text"
            >
              {documentLabel}
            </Text>
          </Tooltip>
          {badges.map((badge) => (
            <Tag key={badge.key} style={{ marginInlineEnd: 0 }}>
              {badge.label}
            </Tag>
          ))}
        </Space>
      );
    case 'type':
      return documentTypeLabel ? (
        <Tooltip title={documentTypeLabel}>
          <Text ellipsis className="cp-composition__ellipsis-text">
            {documentTypeLabel}
          </Text>
        </Tooltip>
      ) : (
        <Text type="secondary">—</Text>
      );
    case 'status':
      return <DocumentStatusBadge status={state} />;
    case 'file':
      return fileName ? (
        <Tooltip title={fileName}>
          <Text
            ellipsis
            type={isExcluded ? 'secondary' : undefined}
            className="cp-composition__ellipsis-text"
          >
            {fileName}
          </Text>
        </Tooltip>
      ) : (
        <Text type="secondary">—</Text>
      );
    case 'route':
      return routeLabel ? (
        <Text type={isExcluded ? 'secondary' : undefined}>{routeLabel}</Text>
      ) : (
        <Text type="secondary">—</Text>
      );
    case 'required':
      return required ? (
        <Tooltip title="Обязательный документ">
          <CheckOutlined aria-label="Обязательный документ" />
        </Tooltip>
      ) : (
        <Tooltip title="Необязательный документ">
          <MinusOutlined aria-label="Необязательный документ" />
        </Tooltip>
      );
    case 'actions':
      return (
        <RowActions
          availableActions={availableActions}
          onAction={(action) => onRowAction?.(action)}
          loading={actionLoading}
        />
      );
    default:
      return null;
  }
};

export default CompositionRow;
